import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Spin } from "antd";
import { LayoutGrid, Mountain } from "lucide-react";
import { useDoubleSidebar, useGlobal } from "@vojomo-core/shared";
import { SessionMountainBkg } from "./components/SessionMountainBkg";
import type { SessionData, AllBuildingBlocks, WMBT } from "../Modus/RoadmapBuilder/types";
import { getBlockDateLabel, formatPeakDate } from "../Modus/RoadmapBuilder/timeframe";
const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

function formatWmbt(items: WMBT[] | null | undefined): string {
  if (!items?.length) return "";
  return items.map((item) => `- **${item.label}**: ${item.value ?? ""}`).join("\n");
}

function getBlockContent(blocks: AllBuildingBlocks, key: keyof AllBuildingBlocks): string {
  const block = blocks[key] as any;
  if (!block) return "";
  if (key === "peakAmbition") {
    return block.pressRelease || block.draft || "";
  }
  if (key === "vision" || key === "mission") {
    return block.draft || "";
  }
  // Objectives blocks — prefer wmbt formatted, fall back to draft
  if (block.wmbt?.length) {
    return formatWmbt(block.wmbt);
  }
  return block.draft || "";
}

// Maps mountain block name → session block key + field to update
const BLOCK_KEY_MAP: Record<string, { key: keyof AllBuildingBlocks; field: string }> = {
  Vision:                    { key: "vision",                 field: "draft" },
  Mission:                   { key: "mission",                field: "draft" },
  "Peak Ambition":           { key: "peakAmbition",           field: "pressRelease" },
  "Peak Ambition Objectives":{ key: "peakAmbitionObjectives", field: "draft" },
  "3 Year Objectives":       { key: "threeYearObjectives",    field: "draft" },
  "1 Year Objectives":       { key: "oneYearObjectives",      field: "draft" },
  "Quarterly Objectives":    { key: "quarterlyObjectives",    field: "draft" },
};

export const MountainView = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  // Solo is the default (matches useRoadmapViewMode); the grid passes ?view=team explicitly.
  const [searchParams] = useSearchParams();
  const viewMode: "solo" | "team" = searchParams.get("view") === "team" ? "team" : "solo";
  const { isCollapsed } = useDoubleSidebar();
  const { isMobile } = useGlobal();
  const [session, setSession] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    setLoading(true);
    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}`, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error("Session not found");
        return r.json();
      })
      .then((data) => {
        setSession(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [sessionId]);

  // Solo reads the viewer's per-user output; Team reads the parent session
  // consensus. Solo falls back to team for legacy data with no viewerOutput
  // (same rule as RoadmapBuilderSession).
  const soloBlocks = session?.viewerOutput?.buildingBlocks ?? session?.buildingBlocks;
  const teamBlocks = session?.buildingBlocks;

  const handleSave = async (blockName: string, content: string) => {
    // PATCH /blocks writes the caller's own output, so only solo edits are valid here.
    if (!session || !sessionId || viewMode !== "solo" || !soloBlocks) return;
    const mapping = BLOCK_KEY_MAP[blockName];
    if (!mapping) return;

    const updatedBlocks: AllBuildingBlocks = {
      ...soloBlocks,
      [mapping.key]: {
        ...(soloBlocks[mapping.key] as any),
        [mapping.field]: content,
      },
    };

    const res = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/blocks`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ buildingBlocks: updatedBlocks }),
    });
    if (!res.ok) throw new Error("Failed to save");
    setSession((prev) => {
      if (!prev) return prev;
      return prev.viewerOutput
        ? { ...prev, viewerOutput: { ...prev.viewerOutput, buildingBlocks: updatedBlocks } }
        : { ...prev, buildingBlocks: updatedBlocks };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <p className="text-muted-foreground">{error || "Session not found"}</p>
        <Button onClick={() => navigate(-1)}>Go back</Button>
      </div>
    );
  }

  const bb = (viewMode === "team" ? teamBlocks : soloBlocks) ?? session.buildingBlocks;
  const ys = { month: session.yearStartMonth ?? 1, day: session.yearStartDay ?? 1 };
  const peakDate = session.peakDate ?? null;
  // Each person's Peak Ambition carries its own date; prefer it over the session-level default.
  const peakDateLabel = bb.peakAmbition?.date || (peakDate ? formatPeakDate(peakDate) : "No Date Yet");

  // Team consensus is edited through the collab flow, not here.
  const hasWriteAccess =
    viewMode === "solo" && (session.viewerOutput ? true : session.userRole === "owner");

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Label — positioned near Vision block (top-left of mountain SVG) */}
      <div
        style={{
          position: "fixed",
          top: 20,
          // Approx x=150/2136 of SVG width from the SVG's left edge, accounting for sidebar
          left: isCollapsed ? "calc(0px + 7vw)" : "calc(190px + 7vw)",
          zIndex: 30,
          display: "flex",
          alignItems: "center",
          gap: 5,
          // Frosted glass — subtle, no opaque white box
          background: "rgba(255,255,255,0.18)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          borderRadius: 6,
          padding: "3px 9px",
          border: "1px solid rgba(255,255,255,0.28)",
        }}
      >
        <Mountain
          className="h-3 w-3"
          style={{ flexShrink: 0, color: "hsl(var(--rb-primary))" }}
        />
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            fontFamily: '"Albert Sans", sans-serif',
            color: "hsl(var(--rb-primary))",
            whiteSpace: "nowrap",
          }}
        >
          {session.companyName} — Mountain View · {viewMode === "team" ? "Team" : "Solo"}
        </span>
      </div>

      {/* View Grid button — mirrors Feedback's right: isMobile ? 12 : 100 */}
      <div style={{ position: "fixed", top: 20, right: isMobile ? 12 : 100, zIndex: 30 }}>
        <Button
          size="small"
          icon={<LayoutGrid className="h-3.5 w-3.5" style={{ flexShrink: 0 }} />}
          onClick={() => navigate(`/roadmap/build_roadmap/${sessionId}`)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontFamily: '"Albert Sans", sans-serif',
            background: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.28)",
            color: "hsl(var(--rb-primary))",
            boxShadow: "none",
          }}
        >
          View Grid
        </Button>
      </div>

      {/* Mountain SVG */}
      <SessionMountainBkg
        vision={getBlockContent(bb, "vision")}
        mission={getBlockContent(bb, "mission")}
        peakAmbitionDate={peakDateLabel}
        peakAmbitionContent={getBlockContent(bb, "peakAmbition")}
        headlineImgUrl={null}
        peakObjectiveDate={peakDateLabel}
        peakObjectiveContent={getBlockContent(bb, "peakAmbitionObjectives")}
        threeYearObjectivesDate={
          getBlockDateLabel("threeYearObjectives", peakDate, ys.month, ys.day) ?? ""
        }
        threeYearObjectivesContent={getBlockContent(bb, "threeYearObjectives")}
        oneYearObjectivesDate={
          getBlockDateLabel("oneYearObjectives", peakDate, ys.month, ys.day) ?? ""
        }
        oneYearObjectivesContent={getBlockContent(bb, "oneYearObjectives")}
        qObjectivesDate={
          getBlockDateLabel("quarterlyObjectives", peakDate, ys.month, ys.day) ?? ""
        }
        qObjectivesContent={getBlockContent(bb, "quarterlyObjectives")}
        hasWriteAccess={hasWriteAccess}
        embedded={false}
        onSave={handleSave}
      />
    </div>
  );
};
